import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import InsightsIcon          from "@mui/icons-material/Insights";
import LanguageIcon          from "@mui/icons-material/Language";
import TouchAppIcon          from "@mui/icons-material/TouchApp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TimerOutlinedIcon     from "@mui/icons-material/TimerOutlined";
import TrendingUpIcon        from "@mui/icons-material/TrendingUp";
import PeopleOutlineIcon     from "@mui/icons-material/PeopleOutline";
import FilterListIcon        from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ErrorOutlineIcon      from "@mui/icons-material/ErrorOutline";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// ─── constants ────────────────────────────────────────────────────────────────

const C = {
  blue:    "#0065DD",
  blueBg:  "rgba(0,101,221,0.12)",
  violet:  "#7c3aed",
  violetBg:"rgba(124,58,237,0.12)",
  green:   "#10b981",
  greenBg: "rgba(16,185,129,0.12)",
  amber:   "#f59e0b",
  amberBg: "rgba(245,158,11,0.12)",
  cyan:    "#06b6d4",
  pink:    "#ec4899",
  slate:   "#64748b",
};

const EMPRESA_LABELS: Record<string, string> = {
  "tradis-falabella":    "Tradis Falabella",
  "falabella-com":       "Falabella.com",
  "blue-express":        "Blue Express",
  "centro-logistico":    "Centro Logístico",
  "centro-distribucion": "Centro Distribución",
  "regiones":            "Regiones",
};

const BAR_OPTS = {
  indexAxis: "y" as const,
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: "bottom" as const }, tooltip: { mode: "index" as const } },
  scales: { x: { stacked: false, grid: { color: "#f1f5f9" } }, y: { grid: { display: false } } },
};

const FUNNEL_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      ticks: { callback: (v: number | string) => `${v}%` },
      grid: { color: "#f1f5f9" },
    },
    x: { grid: { display: false } },
  },
};

const DONUT_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "66%",
  plugins: {
    legend: { position: "bottom" as const, labels: { boxWidth: 12, padding: 14, font: { size: 12 } } },
  },
};

// ─── types ────────────────────────────────────────────────────────────────────

interface ILandingMetrics {
  TotalSesiones: number;
  SesionesQuePostularon: number;
  TasaConversionPct: number;
  DuracionPromedioSeg: number;
}

interface IEmpresaMetrics {
  EmpresaId: string;
  CliquesBadge: number;
  CliquesNav: number;
  CliquesPostular: number;
  TotalInteracciones: number;
}

interface IFormMetrics {
  TotalSesiones: number;
  SesionesCompletadas: number;
  SesionesAbandonadas: number;
  TasaConversionPct: number;
  DuracionPromedioSegCompletadas: number;
  PasoPromedioAlcanzado: number;
  SesionesMovil: number;
  SesionesTablet: number;
  SesionesDesktop: number;
}

interface IFunnelStep {
  Paso: number;
  UsuariosEnPaso: number;
  RetencionPct: number;
}

interface IFormItem {
  contextFormId: string;
  client: string;
  vacancyId?: string;
  recruiter?: string;
  formType?: string;
  totalApplications?: number;
}

// ─── sub-components ───────────────────────────────────────────────────────────

const KpiCard = ({
  icon,
  label,
  value,
  sub,
  color,
  bg,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
  bg: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    style={{
      flex: "1 1 160px",
      minWidth: 140,
      background: "#fff",
      borderRadius: 12,
      padding: "16px 18px",
      borderLeft: `4px solid ${color}`,
      boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ color, background: bg, borderRadius: 8, padding: 5, display: "flex" }}>
        {icon}
      </span>
      <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>{label}</span>
    </div>
    <span style={{ fontSize: 26, fontWeight: 700, color: "#1e293b", lineHeight: 1.1 }}>{value}</span>
    {sub && <span style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</span>}
  </motion.div>
);

const SectionHeader = ({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
    <span style={{ color: C.blue, background: C.blueBg, borderRadius: 10, padding: 8, display: "flex" }}>
      {icon}
    </span>
    <div>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#1e293b" }}>{title}</h2>
      <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>{subtitle}</p>
    </div>
  </div>
);

const ChartCard = ({ title, children, height = 240 }: { title: string; children: React.ReactNode; height?: number }) => (
  <div
    style={{
      flex: "1 1 300px",
      background: "#fff",
      borderRadius: 12,
      padding: "16px 18px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    }}
  >
    <p style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 600, color: "#475569" }}>{title}</p>
    <div style={{ height }}>{children}</div>
  </div>
);

const EmptyState = ({ message }: { message: string }) => (
  <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
    <ErrorOutlineIcon style={{ fontSize: 40, marginBottom: 8, display: "block", margin: "0 auto 8px" }} />
    <p style={{ margin: 0, fontSize: 14 }}>{message}</p>
  </div>
);

const Shimmer = ({ h = 80 }: { h?: number }) => (
  <div
    style={{
      height: h,
      borderRadius: 12,
      background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s infinite",
    }}
  />
);

// ─── main component ───────────────────────────────────────────────────────────

const PanelMetricas: React.FC = () => {
  const base = process.env.REACT_APP_API_APPLICANT ?? "";

  // ── landing state ──────────────────────────────────────────────────────────
  const [landingMetrics, setLandingMetrics] = useState<ILandingMetrics | null>(null);
  const [empresas, setEmpresas]             = useState<IEmpresaMetrics[]>([]);
  const [landingLoading, setLandingLoading] = useState(true);
  const [landingError, setLandingError]     = useState<string | null>(null);

  // ── forms selector state ───────────────────────────────────────────────────
  const [forms, setForms]               = useState<IFormItem[]>([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState<string>("");

  // ── per-form metrics state ─────────────────────────────────────────────────
  const [formMetrics, setFormMetrics]   = useState<IFormMetrics | null>(null);
  const [funnel, setFunnel]             = useState<IFunnelStep[]>([]);
  const [formLoading, setFormLoading]   = useState(false);
  const [formError, setFormError]       = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // ── fetch landing metrics ──────────────────────────────────────────────────
  useEffect(() => {
    if (!base) { setLandingError("REACT_APP_API_APPLICANT no configurado"); setLandingLoading(false); return; }
    fetch(`${base}/tracking/landing-metrics`)
      .then((r) => r.json())
      .then((d) => {
        setLandingMetrics(d.metrics ?? null);
        setEmpresas(Array.isArray(d.empresas) ? d.empresas : []);
      })
      .catch(() => setLandingError("No se pudieron cargar las métricas de la landing."))
      .finally(() => setLandingLoading(false));
  }, [base]);

  // ── fetch forms list for selector ─────────────────────────────────────────
  useEffect(() => {
    if (!base) { setFormsLoading(false); return; }
    const userId = sessionStorage.getItem("usuarioId") ?? "";
    fetch(`${base}/form/list?page=1&limit=200&userId=${encodeURIComponent(userId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filters: {} }),
    })
      .then((r) => r.json())
      .then((d) => setForms(Array.isArray(d?.forms) ? d.forms : []))
      .catch(() => setForms([]))
      .finally(() => setFormsLoading(false));
  }, [base]);

  // ── fetch per-form metrics when selection changes ──────────────────────────
  useEffect(() => {
    if (!selectedForm || !base) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setFormLoading(true);
    setFormError(null);
    setFormMetrics(null);
    setFunnel([]);

    Promise.all([
      fetch(`${base}/tracking/metrics?contextFormId=${encodeURIComponent(selectedForm)}`, { signal: ctrl.signal }).then((r) => r.json()),
      fetch(`${base}/tracking/funnel?contextFormId=${encodeURIComponent(selectedForm)}`, { signal: ctrl.signal }).then((r) => r.json()),
    ])
      .then(([mRes, fRes]) => {
        setFormMetrics(mRes.metrics ?? null);
        setFunnel(Array.isArray(fRes.funnel) ? fRes.funnel : []);
      })
      .catch((e) => { if (e.name !== "AbortError") setFormError("No se pudieron cargar las métricas del formulario."); })
      .finally(() => setFormLoading(false));
  }, [selectedForm, base]);

  // ── derived chart data ─────────────────────────────────────────────────────

  const empresasChartData = {
    labels: empresas.map((e) => EMPRESA_LABELS[e.EmpresaId] ?? e.EmpresaId),
    datasets: [
      { label: "Badge (hero)",     data: empresas.map((e) => e.CliquesBadge),    backgroundColor: C.blue,   borderRadius: 4 },
      { label: "Nav (secciones)",  data: empresas.map((e) => e.CliquesNav),      backgroundColor: C.violet, borderRadius: 4 },
      { label: "Postular",         data: empresas.map((e) => e.CliquesPostular), backgroundColor: C.green,  borderRadius: 4 },
    ],
  };

  const landingDeviceData = {
    labels: ["Móvil", "Escritorio", "Tablet", "Desconocido"],
    datasets: [{
      data: [0, 0, 0, 0], // populated below
      backgroundColor: [C.blue, C.violet, C.green, "#e2e8f0"],
      borderWidth: 0,
    }],
  };

  const funnelChartData = {
    labels: funnel.map((s) => `Paso ${s.Paso}`),
    datasets: [{
      label: "Retención %",
      data: funnel.map((s) => Number(s.RetencionPct)),
      backgroundColor: funnel.map((_, i) => {
        const opacity = 1 - (i * 0.12);
        return `rgba(0,101,221,${Math.max(0.3, opacity)})`;
      }),
      borderRadius: 6,
    }],
  };

  const formDeviceData = formMetrics
    ? {
        labels: ["Móvil", "Escritorio", "Tablet"],
        datasets: [{
          data: [formMetrics.SesionesMovil, formMetrics.SesionesDesktop, formMetrics.SesionesTablet],
          backgroundColor: [C.blue, C.violet, C.green],
          borderWidth: 0,
        }],
      }
    : null;

  const selectedFormItem = forms.find((f) => f.contextFormId === selectedForm);

  const fmt = (n?: number | null) => n == null ? "—" : n.toLocaleString("es-CL");
  const fmtPct = (n?: number | null) => n == null ? "—" : `${Number(n).toFixed(1)}%`;
  const fmtMin = (s?: number | null) => s == null ? "—" : `${Math.round(s / 60)} min`;

  return (
    <div style={{ padding: "8px 0 40px", fontFamily: "Roboto, sans-serif" }}>

      {/* shimmer style */}
      <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>

      {/* ═══════════════════════════════════════════ LANDING ══════════════════ */}
      <div
        style={{
          background: "#f8fafc",
          borderRadius: 16,
          padding: "24px",
          marginBottom: 28,
          border: "1px solid #e9eef5",
        }}
      >
        <SectionHeader
          icon={<LanguageIcon style={{ fontSize: 22 }} />}
          title="Landing page"
          subtitle="Actividad y conversiones de la página pública de vacantes"
        />

        {landingLoading ? (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[1,2,3,4].map((i) => <div key={i} style={{ flex: "1 1 160px" }}><Shimmer h={90} /></div>)}
          </div>
        ) : landingError ? (
          <EmptyState message={landingError} />
        ) : !landingMetrics || Number(landingMetrics.TotalSesiones) === 0 ? (
          <EmptyState message="Aún no hay datos de visitas registrados para la landing." />
        ) : (
          <>
            {/* KPI row */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
              <KpiCard icon={<PeopleOutlineIcon style={{ fontSize: 20 }} />}  label="Total visitas"    value={fmt(landingMetrics.TotalSesiones)}         color={C.blue}   bg={C.blueBg}   />
              <KpiCard icon={<TouchAppIcon style={{ fontSize: 20 }} />}       label="Postularon"       value={fmt(landingMetrics.SesionesQuePostularon)}  color={C.green}  bg={C.greenBg}  />
              <KpiCard icon={<TrendingUpIcon style={{ fontSize: 20 }} />}     label="Tasa conversión"  value={fmtPct(landingMetrics.TasaConversionPct)}   color={C.violet} bg={C.violetBg} />
              <KpiCard icon={<TimerOutlinedIcon style={{ fontSize: 20 }} />}  label="Duración promedio" value={fmtMin(landingMetrics.DuracionPromedioSeg)} color={C.amber}  bg={C.amberBg}  sub="tiempo en la página" />
            </div>

            {/* Charts row */}
            {empresas.length > 0 && (
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <ChartCard title="Interacciones por empresa" height={Math.max(160, empresas.length * 52)}>
                  <Bar data={empresasChartData} options={BAR_OPTS} />
                </ChartCard>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════ FORMULARIOS ══════════════════ */}
      <div
        style={{
          background: "#f8fafc",
          borderRadius: 16,
          padding: "24px",
          border: "1px solid #e9eef5",
        }}
      >
        <SectionHeader
          icon={<InsightsIcon style={{ fontSize: 22 }} />}
          title="Formularios de postulación"
          subtitle="Métricas de sesión, embudo de conversión y dispositivos por formulario"
        />

        {/* form selector */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>
            <FilterListIcon style={{ fontSize: 15, verticalAlign: "middle", marginRight: 4 }} />
            Seleccionar formulario
          </label>
          {formsLoading ? (
            <Shimmer h={40} />
          ) : (
            <div style={{ position: "relative", maxWidth: 500 }}>
              <select
                value={selectedForm}
                onChange={(e) => setSelectedForm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 36px 10px 14px",
                  borderRadius: 10,
                  border: "1.5px solid #e2e8f0",
                  background: "#fff",
                  fontSize: 14,
                  color: "#1e293b",
                  appearance: "none",
                  cursor: "pointer",
                  outline: "none",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <option value="">— Selecciona un formulario —</option>
                {forms.map((f) => (
                  <option key={f.contextFormId} value={f.contextFormId}>
                    {f.client ? `${f.client}${f.vacancyId ? ` · ${f.vacancyId}` : ""}` : f.contextFormId}
                  </option>
                ))}
              </select>
              <KeyboardArrowDownIcon
                style={{
                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                  color: "#94a3b8", pointerEvents: "none", fontSize: 20,
                }}
              />
            </div>
          )}
        </div>

        {/* per-form content */}
        <AnimatePresence mode="wait">
          {!selectedForm ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <EmptyState message="Selecciona un formulario para ver sus métricas de seguimiento." />
            </motion.div>
          ) : formLoading ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                {[1,2,3,4,5].map((i) => <div key={i} style={{ flex: "1 1 140px" }}><Shimmer h={90} /></div>)}
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 280px" }}><Shimmer h={240} /></div>
                <div style={{ flex: "1 1 220px" }}><Shimmer h={240} /></div>
              </div>
            </motion.div>
          ) : formError ? (
            <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <EmptyState message={formError} />
            </motion.div>
          ) : !formMetrics || Number(formMetrics.TotalSesiones) === 0 ? (
            <motion.div key="no-data" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <EmptyState message="Este formulario aún no tiene sesiones registradas." />
            </motion.div>
          ) : (
            <motion.div key={selectedForm} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>

              {/* form info badge */}
              {selectedFormItem && (
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ background: C.blueBg, color: C.blue, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
                    {selectedFormItem.client || "—"}
                  </span>
                  {selectedFormItem.vacancyId && (
                    <span style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 500 }}>
                      {selectedFormItem.vacancyId}
                    </span>
                  )}
                  {selectedFormItem.recruiter && (
                    <span style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 20, padding: "4px 12px", fontSize: 12 }}>
                      {selectedFormItem.recruiter}
                    </span>
                  )}
                </div>
              )}

              {/* KPI row */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
                <KpiCard icon={<PeopleOutlineIcon style={{ fontSize: 20 }} />}      label="Total sesiones"   value={fmt(formMetrics.TotalSesiones)}                  color={C.blue}   bg={C.blueBg}   />
                <KpiCard icon={<CheckCircleOutlineIcon style={{ fontSize: 20 }} />} label="Completados"      value={fmt(formMetrics.SesionesCompletadas)}             color={C.green}  bg={C.greenBg}  />
                <KpiCard icon={<TrendingUpIcon style={{ fontSize: 20 }} />}         label="Tasa conversión"  value={fmtPct(formMetrics.TasaConversionPct)}            color={C.violet} bg={C.violetBg} />
                <KpiCard icon={<TimerOutlinedIcon style={{ fontSize: 20 }} />}      label="Duración promedio" value={fmtMin(formMetrics.DuracionPromedioSegCompletadas)} color={C.amber} bg={C.amberBg}  sub="sesiones completadas" />
                <KpiCard icon={<InsightsIcon style={{ fontSize: 20 }} />}            label="Paso promedio"    value={formMetrics.PasoPromedioAlcanzado != null ? Number(formMetrics.PasoPromedioAlcanzado).toFixed(1) : "—"} color={C.cyan} bg="rgba(6,182,212,0.12)" sub="de 6 pasos" />
              </div>

              {/* charts row */}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {funnel.length > 0 && (
                  <ChartCard title="Embudo de conversión — retención por paso" height={240}>
                    <Bar data={funnelChartData} options={FUNNEL_OPTS} />
                  </ChartCard>
                )}
                {formDeviceData && (
                  <ChartCard title="Distribución por dispositivo" height={240}>
                    <Doughnut data={formDeviceData} options={DONUT_OPTS} />
                  </ChartCard>
                )}
              </div>

            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PanelMetricas;
